<script lang="ts" setup>
import { SwiperSlide } from 'swiper/vue'
import { useStoreConfig } from '~/store/storeConfig'
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import { useCategoryStore } from '~/store/categoryStore'
import type { CategoryItem, Product, RawProduct } from '~/types/searchTypes'
import { SortDirection } from '~/types/searchTypes'

import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'
import { useLang } from '~/composables/useLang'
import { useLanguageStore } from '~/store/language'
import computeAllCategoryChildrenIdsByParentIds from '~/utils/config/search/computeAllCategoryChildrenIdsByParentIds'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import parseProductSlug from '~/utils/parseProductSlug'
import randomizeArray from '~/utils/randomizeArray'
import type { CarouselProduct } from '~/types/Product'
import getUniqueProducts from '~/utils/search/getUniqueProducts'
import { fetchRetryParameters, requestHostHeaders } from '~/composables/constants/api'
import type { RequestHostHeadersType } from '~/composables/constants/api'

const { t } = useLang()

const props = defineProps<{
  backgroundColor?: string
  offersBanner: {
    slug: TranslatableString
    backgroundMobile: TranslatableString
    backgroundDesktop: TranslatableString
  }
  slider: {
    discount?: number
    categories?: number[]
    sort: keyof typeof SortDirection
    manufacturer?: string[]
    limit: number
    randomizeProducts?: boolean
    lockedProducts?: {
      productSlug: TranslatableString
    }[]
  }
}>()

const products = ref<Product[] | CarouselProduct[]>([])
const config = useStoreConfig()
const categoryStore = useCategoryStore()
const locale = useState<string>('locale.setting')

const backgroundImage = computed(() => {
  if (config.isTablet) {
    return props.offersBanner.backgroundDesktop
  }

  return config.isMobile ? props.offersBanner.backgroundMobile : props.offersBanner.backgroundDesktop
})

async function fetchOffers(categoryItems: CategoryItem[]): Promise<void> {
  try {
    let categories: undefined | number[]

    if (props.slider.categories) {
      categories = computeAllCategoryChildrenIdsByParentIds(props.slider.categories, categoryItems)
    }

    const response = await $fetch<RawProduct[]>('/api/hooks/widgetProducts', {
      method: 'POST',
      body: {
        locale: locale.value,
        limit: props.slider.limit,
        manufacturer: props.slider.manufacturer,
        categories,
        sort: props.slider.sort,
        discount: props.slider.discount,
        languageId: useLanguageStore().getLanguageId,
      },
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    })

    const fResponse = getUniqueProducts(response)

    if (props.slider.lockedProducts?.length) {
      const productsRes = await $fetch('/api/market/products', {
        method: 'POST',
        body: {
          products: props.slider.lockedProducts.map((lockedProduct) => parseProductSlug(lockedProduct.productSlug)),
          langId: useLanguageStore().getLanguageId,
        },
        ...fetchRetryParameters,
        timeout: 500 + props.slider.lockedProducts.length * 900,
        headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
      })

      const lockedProducts = productsRes.filter((product) => product?.id && !product?.is_sold_out) as CarouselProduct[]

      const unlockedProducts = props.slider.randomizeProducts
        ? randomizeArray(fResponse, props.slider.limit - lockedProducts.length)
        : fResponse.slice(0, props.slider.limit - lockedProducts.length)

      products.value = [...lockedProducts, ...unlockedProducts]
    } else {
      products.value = props.slider.randomizeProducts ? randomizeArray(fResponse, props.slider.limit) : fResponse
    }
  } catch (error) {}
}

watch(
  (): [CategoryItem[]] => [categoryStore.getCategories],
  async ([categoryItems]) => {
    await fetchOffers(categoryItems)
  },
  {
    immediate: true,
  }
)

const key = ref(0)

onMounted(() => {
  // Manually rerendering component
  setTimeout(() => {
    key.value++
  }, 300)
})
</script>

<template>
  <div :key="key">
    <section v-if="Boolean(products?.length)" class="offer-slider" :style="{ backgroundColor: props.backgroundColor }">
      <ClientOnly>
        <div v-if="config.isMobile && !config.isTablet" class="p-2">
          <LazyStoreBanner
            :background-desktop="props.offersBanner.backgroundDesktop"
            :background-mobile="props.offersBanner.backgroundMobile"
            banner-style="Full"
            :button-text="t('common.choose-products')"
            button-style="Black"
            :slug="props.offersBanner.slug"
            :open-links-in-new-tab="false" />
        </div>
        <FullSlider manual-height>
          <template #sliderContent>
            <SwiperSlide
              v-if="!config.isMobile || config.isTablet"
              key="banner-slide"
              class="md:!h-[560px] lg:!w-[381px]">
              <div
                class="banner-card flex flex-col justify-end h-full p-4 lg:p-8 relative"
                :style="{
                  'background-image': `url(${getWidgetTranslatedMediaUrl(backgroundImage)})`,
                }">
                <NuxtLink
                  :to="getWidgetTextTranslation(props.offersBanner.slug) || '/'"
                  class="absolute h-full w-full z-10 top-0 left-0" />
                <NuxtLink
                  :to="getWidgetTextTranslation(props.offersBanner.slug) || '/'"
                  class="btn-secondary black-variant w-full z-20">
                  {{ t('common.choose-products') }}
                  <i class="fas fa-arrow-right" />
                </NuxtLink>
              </div>
            </SwiperSlide>
            <SwiperSlide v-for="(product, index) in products" :key="index">
              <SliderCard v-bind="product" />
            </SwiperSlide>
          </template>
        </FullSlider>
      </ClientOnly>
    </section>
  </div>
</template>

<style lang="postcss" scoped>
.offer-slider {
  :deep(.full-slider) {
    padding-top: 1rem;

    .swiper-wrapper {
      @apply h-[470px] md:!h-[560px];
    }

    .swiper-slide {
      height: 100%;
    }
  }
}

.banner-card {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top;
  border-radius: theme('vars.borderRadiusLight');

  a > i {
    margin-left: 1rem;
  }
}

.alt .banner-card {
  @apply rounded-r-none rounded-l-sm;
}
.wl .banner-card {
  @apply rounded-md;
}
</style>
